import {
  CalendarFilled,
  CloseCircleFilled,
  CreditCardFilled,
  DeleteFilled,
  EditFilled,
  FileImageFilled,
  MoneyCollectFilled,
  RocketFilled,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Tooltip } from "antd";
import dayjs from "dayjs";
import { Group } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMoveExpenseToMisc } from "../../../api/api";
import { useDeleteExpense } from "../../../api/api.delete";
import {
  useGetAccountById,
  useGetExpenseById2,
  useGetGroups2,
  useGetGroupUsers2,
} from "../../../api/api.get";
import ConfirmDialog from "../../../components/dialog/confirm.dialog";
import SaveAsTripExpenseDialog from "../../../components/dialog/saveas.trip.expense.dialog";
import { useNotifications } from "../../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useAuthContext } from "../../../providers/auth.provider";
import { useCurrencyContext } from "../../../providers/currency.provider";
import { IExpense2 } from "../../../types/types";
import { capitalizeFirst } from "../../../utils/functions";
import { toFixedDisplay } from "../../../utils/number.utils";

export default function ExpenseViewSection({
  onEdit: onToggleEdit,
  groupId,
  expenseId,
  hideToolBar = false,
}: {
  hideToolBar?: boolean;
  groupId?: string;
  expenseId?: string;
  onEdit: () => void;
}) {
  const { data: groupsData } = useGetGroups2();
  const { user } = useAuthContext();
  const { data: groupUsersData } = useGetGroupUsers2(groupId);

  // Sample monthly spending trend
  const {
    data: expenseData,
    error,
    refetch,
  } = useGetExpenseById2(groupId, expenseId);

  const { data: creditCardData } = useGetAccountById(
    expenseData?.creditcard,
    expenseData?.username === user?.username,
  );
  const { currency } = useCurrencyContext();
  const { notification, notifyError, notifySuccess } = useNotifications();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { mutateAsync: deleteAsync } = useDeleteExpense();

  const handleDeleteAsync = () => {
    deleteAsync({ expenseId: expenseData!.id })
      .then(() => setShowDeleteConfirm(false))
      .catch(() => notifyError("Whoops! Please try again."));
  };

  const [saveAsMiscExpense, setSaveAsMiscExpense] = useState<
    IExpense2 | undefined
  >(undefined);

  const { mutateAsync: saveAsMiscExpenseAsync } = useMoveExpenseToMisc(groupId);
  const [saveAsTripExpense, setSaveAsTripExpense] = useState<
    IExpense2 | undefined
  >(undefined);

  const handleSaveAsMiscExpense = (expense: IExpense2) => {
    saveAsMiscExpenseAsync({
      expenseId: expense.id,
    })
      .then(() => {
        notifySuccess({
          description: "Expense saved as Misc successfully",
        });

        refetch();
      })
      .catch(() => {
        notifyError("Something went wrong. Please try again.");
      })
      .finally(() => {
        setSaveAsMiscExpense(undefined);
      });
  };

  const expenseUser = useMemo(() => {
    if (groupUsersData && expenseData) {
      return groupUsersData.find((gu) => gu.username === expenseData.username);
    }
    return undefined;
  }, [expenseData, groupUsersData]);

  const groupRef = useMemo(() => {
    if (groupsData && expenseData) {
      return groupsData.find((g) => g.id === expenseData.groupId);
    }
    return undefined;
  }, [expenseData, groupsData]);

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      {showDeleteConfirm && (
        <ConfirmDialog
          open={showDeleteConfirm}
          onOk={handleDeleteAsync}
          title="Are you sure you want to delete?"
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
      {saveAsMiscExpense && (
        <ConfirmDialog
          open={!!saveAsMiscExpense}
          onCancel={() => setSaveAsMiscExpense(undefined)}
          onOk={() => handleSaveAsMiscExpense(saveAsMiscExpense!)}
          title={"Are you sure you want to convert this expense to Misc?"}
        />
      )}
      {saveAsTripExpense && (
        <SaveAsTripExpenseDialog
          open={!!saveAsTripExpense}
          expense={saveAsTripExpense}
          onCancel={() => setSaveAsTripExpense(undefined)}
          onOk={async () => {
            setSaveAsTripExpense(undefined);
            notifySuccess({ description: "Expenses successfully changed" });
            await refetch();
          }}
        />
      )}
      {/* Main Content */}
      <main className="pt-4 pb-12 px-4 sm:px-6 lg:px-8">
        {expenseData && (
          <div className="max-w-4xl mx-auto">
            {/* Top Navigation */}
            {!hideToolBar && (
              <div className="mb-8 flex justify-end items-center">
                <div className="flex gap-2 flex-wrap justify-end">
                  <Tooltip title="Change to Misc">
                    <Button
                      type="text"
                      icon={<MoneyCollectFilled />}
                      disabled={
                        expenseData?.type === "misc" ||
                        expenseData?.username !== user?.username
                      }
                      onClick={() => setSaveAsMiscExpense(expenseData)}
                    />
                  </Tooltip>

                  <Tooltip title="Change to Trip Expense">
                    <Button
                      type="text"
                      icon={<RocketFilled />}
                      disabled={expenseData?.username !== user?.username}
                      onClick={() => setSaveAsTripExpense(expenseData)}
                    />
                  </Tooltip>

                  <Tooltip title="Change Group">
                    <Button
                      type="text"
                      icon={<UsergroupAddOutlined />}
                      disabled={expenseData?.username !== user?.username}
                    />
                  </Tooltip>

                  <Button
                    type="text"
                    icon={<EditFilled />}
                    onClick={() => onToggleEdit()}
                    disabled={expenseData?.username !== user?.username}
                  />
                  <Button
                    type="text"
                    icon={
                      <DeleteFilled
                        style={{
                          color:
                            expenseData?.username !== user?.username
                              ? undefined
                              : "red",
                        }}
                      />
                    }
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={expenseData?.username !== user?.username}
                  />
                </div>
              </div>
            )}

            {/* Main Header Card */}
            <Card className="bg-white mb-6 border-2">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Left: Expense Title & Amount */}
                  <div className="md:col-span-2">
                    <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-2">
                      {expenseData?.category}
                    </p>
                    <h1 className="text-4xl font-bold text-foreground mb-4">
                      {capitalizeFirst(expenseData?.subcategory)}
                    </h1>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {expenseData?.plaidCategories?.split(",").map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-start flex-col gap-2">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CalendarFilled className="w-4 h-4" />
                        <span>
                          {dayjs(expenseData?.expenseDate).format(
                            "DD MMM YYYY",
                          )}
                        </span>
                      </div>
                      {expenseData?.type === "misc" && (
                        <div
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-amber-400 gap-2 flex`}
                        >
                          <MoneyCollectFilled />
                          <span>Misc</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Amount Box */}
                  <div className="bg-blue-50 rounded-xl p-4 sm:p-6 flex flex-col justify-center items-center text-center">
                    <p className="text-sm text-muted-foreground mb-2">
                      Total Amount
                    </p>
                    <p
                      className="text-3xl sm:text-5xl font-bold mb-2 break-all"
                      style={{ color: "var(--exhut-primary)" }}
                    >
                      {currency}
                      {toFixedDisplay(expenseData?.amount)}
                    </p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700`}
                    >
                      Completed
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Three Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Left Column: Receipt & Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Receipt Section */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileImageFilled className="w-5 h-5 text-primary" />
                      Receipt
                    </CardTitle>
                    <CardDescription>No Receipt Available</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-100 rounded-lg p-12 text-center border-2 border-dashed border-slate-300 min-h-80 flex items-center justify-center">
                      <div>
                        <div className="text-6xl text-slate-400 mb-4">📄</div>
                        <p className="text-muted-foreground font-medium">
                          Receipt image would appear here
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Click to view full receipt
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Payment & Split Info */}
              <div className="space-y-6">
                <div className="lg:col-span-1 space-y-6">
                  {/* Owner or User */}
                  <Card className="bg-white">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserOutlined className="w-5 h-5 text-primary" />
                        Owner
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="font-semibold text-foreground text-gray-600 text-sm">
                          {expenseUser?.lastName ?? "-"},{" "}
                          {expenseUser?.firstName ?? "-"}
                        </p>
                        <div className="mt-6">
                          <p className="text-xs font-semibold text-muted-foreground uppercase my-2">
                            Group Name:
                          </p>
                          <p className="font-semibold text-foreground text-gray-600 text-sm">
                            {groupRef?.name ?? "-"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {
                    /* get account by id won't work if you are seeing other persons expense. So use this only for self expense */
                    expenseData.username === user?.username && (
                      <Card className="bg-white">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Group className="w-5 h-5 text-primary" />
                            Account
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="">
                            <p className="text-xs font-semibold text-muted-foreground uppercase my-2">
                              Account Name:
                            </p>
                            <p className="font-semibold text-foreground text-gray-600 text-sm">
                              {creditCardData?.name ?? "-- Not Found --"}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  }
                </div>

                {/* Payment Method */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCardFilled className="w-5 h-5 text-primary" />
                      Payment Method
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                        Payment Channel
                      </p>
                      <p className="font-semibold text-foreground text-gray-600">
                        {expenseData?.paymentChannel ?? "-"}
                      </p>

                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                          Merchant Name
                        </p>

                        <p className="font-semibold text-foreground mb-4">
                          {expenseData?.merchantLogo && (
                            <Avatar
                              src={expenseData?.merchantLogo}
                              size={"small"}
                            />
                          )}
                          {expenseData?.merchantName ?? "-"}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          {expenseData?.location}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Notes Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground leading-relaxed">
                  {expenseData?.comments}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
        {error && (
          <div className="flex mt-10 justify-center items-center">
            <p className="bg-red-100 border border-red-200 p-3 flex gap-2 items-center rounded-lg text-gray-600 text-sm">
              <CloseCircleFilled className="text-red-500 text-xl" />
              This item is not found. Please click{" "}
              <Link className="text-blue-500 font-bold" to={"/home"}>
                here
              </Link>{" "}
              to go back.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
